import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { getErrorMessage } from '../../utils/errors';
import { OAUTH_BASE_URL } from '../../config/constants';
import { Input } from '../../components/common/Input/Input';
import { Button } from '../../components/common/Button/Button';
import './Auth.css';


type LoginField = 'email' | 'password';
type LoginErrors = Partial<Record<LoginField, string>>;
type LoginTouched = Partial<Record<LoginField, boolean>>;

function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<LoginErrors>({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | null>(null);
  const [touched, setTouched] = useState<LoginTouched>({});


  useEffect(() => {
    if (searchParams.get('error') === 'oauth_failed') {
      setServerError(t('auth.oauthFailed'));
    }
  }, [searchParams, t]);

  const validate = (): LoginErrors => {
    const errs: LoginErrors = {};
    if (!email.trim()) {
      errs.email = t('auth.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = t('auth.validation.emailInvalid');
    }
    if (!password) {
      errs.password = t('auth.validation.passwordRequired');
    } else if (password.length < 6) {
      errs.password = t('auth.validation.passwordMin');
    }
    return errs;
  };

  const handleBlur = (field: LoginField) => {
    setTouched({ ...touched, [field]: true });
    const errs = validate();
    setErrors((prev) => ({ ...prev, [field]: errs[field] || '' }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setServerError('');
    const errs = validate();
    setErrors(errs);
    setTouched({ email: true, password: true });
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.loginSuccess'));
      navigate('/');
    } catch (error) {
      setServerError(await getErrorMessage(error, t('auth.loginFailed')));
    } finally {
      setLoading(false);
    }
  };

  const isValid = (field: LoginField): boolean =>
    Boolean(touched[field] && !errors[field] && (field === 'email' ? email.trim() : password));

  return (
    <main className="auth-page">
      <div className="auth-card">
        <img src="/logo.png" alt="FCS" className="auth-logo" />
        <h1>{t('auth.login')}</h1>
        <p className="auth-subtitle">{t('auth.loginSubtitle')}</p>
        {serverError && <p className="auth-error">{serverError}</p>}

        <div className="auth-social">
          <a href={`${OAUTH_BASE_URL}/api/auth/oauth2/google`}
             className={`auth-social-btn auth-google-btn ${oauthLoading === 'google' ? 'auth-social-loading' : ''}`}
             onClick={() => setOauthLoading('google')}>
            {oauthLoading === 'google' ? (
              <span className="auth-spinner auth-spinner-dark"></span>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            )}
            {t('auth.continueGoogle')}
          </a>
        </div>

        <div className="auth-divider">
          <span>{t('auth.or')}</span>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <Input
            id="email"
            type="email"
            label={t('auth.email')}
            placeholder="example@email.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (touched.email) { const errs = validate(); setErrors((prev) => ({ ...prev, email: errs.email || '' })); } }}
            onBlur={() => handleBlur('email')}
            error={errors.email}
            isTouched={touched.email}
            isValid={isValid('email')}
            floating={true}
            className="form-group"
          />

          <Input
            id="password"
            type="password"
            label={t('auth.password')}
            placeholder="••••••••"
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (touched.password) { const errs = validate(); setErrors((prev) => ({ ...prev, password: errs.password || '' })); } }}
            onBlur={() => handleBlur('password')}
            error={errors.password}
            isTouched={touched.password}
            isValid={isValid('password')}
            floating={true}
            className="form-group"
          />

          <Button
            type="submit"
            className="auth-submit-btn"
            loading={loading}
          >
            {t('auth.login')}
          </Button>
        </form>
        <p className="auth-forgot"><Link to="/forgot-password">{t('auth.forgotPassword')}</Link></p>
        <p className="auth-footer">{t('auth.noAccount')} <Link to="/register">{t('auth.register')}</Link></p>
      </div>
    </main>
  );
}

export default Login;
